import React from "react";
import { Popper, Paper } from "@mui/material";
import { usePopoutWindowContext } from "../../../shared/components/PopoutWindow";

/**
 * A cursor-following tooltip built on MUI `Popper`.
 *
 * It portals into the pop-out window's `<body>` (via the PopoutWindow context) so it renders in the
 * correct window, and Popper handles placement + flipping/overflow. Styling comes from MUI `Paper`
 * (the pop-out has its own Emotion cache, so MUI styles apply there).
 */
type Props = {
    open: boolean;
    x: number;
    y: number;
    children: React.ReactNode;
};

const MODIFIERS = [
    { name: "offset", options: { offset: [12, 12] } },
    { name: "preventOverflow", options: { padding: 8 } },
    { name: "flip", enabled: true },
];

export function ProfilingTooltip({ open, x, y, children }: Props) {
    const container = usePopoutWindowContext()?.window?.document.body ?? undefined;

    // Virtual anchor positioned at the pointer — Popper lays the tooltip out relative to it.
    const anchorEl = React.useMemo(
        () => ({
            getBoundingClientRect: () =>
                ({ x, y, top: y, left: x, right: x, bottom: y, width: 0, height: 0, toJSON: () => ({}) } as DOMRect),
        }),
        [x, y]
    );

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            container={container}
            placement="bottom-start"
            modifiers={MODIFIERS}
            style={{ pointerEvents: "none", zIndex: 2000 }}
        >
            <Paper elevation={6} sx={{ px: 1.25, py: 1, minWidth: 180, maxWidth: 340, fontSize: "0.72rem", lineHeight: 1.5 }}>
                {children}
            </Paper>
        </Popper>
    );
}

// --- Tooltip content pieces --------------------------------------------------

export function TipTitle({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ fontWeight: 600, fontSize: "0.78rem", marginBottom: 6, wordBreak: "break-word" }}>
            {children}
        </div>
    );
}

export function TipRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, lineHeight: 1.5 }}>
            <span style={{ color: "var(--colors-grey-3, #898781)", flex: "0 0 auto" }}>{label}</span>
            <span style={{ fontVariantNumeric: "tabular-nums", textAlign: "right", wordBreak: "break-word" }}>{value}</span>
        </div>
    );
}

export function TipDivider() {
    return <div style={{ height: 1, background: "var(--border-color, rgba(128,128,128,0.35))", margin: "6px 0" }} />;
}
